import { ensureDefined } from '@avantia/client-and-server-utilities';
import { HpAnswerMap } from '@avantia/questionset-model';
import { HpSvgIconTypes, SvgIcon } from '@avantia/style-guide';
import { Row } from 'react-bootstrap';
import { createStandardFormEvent } from '../../../engineClient/components/formControls/inputControl';
import { HpButtonListControlProps } from '../../../engineClient/components/formControls/standard';
import { ImageWidget } from '../../../engineClient/components/widgets';
import { HpClientQuestionData } from '../../../engineClient/types';
import { HpClaimsCommonCustomData } from '../../../tools/utilities';
import { Policy } from '../../../types/policyTypes';
import { questionIds } from '../../../types/questionConstants';
import { HpClaimsQuestionSetPageProps } from '../../questionSetForm/questionSetPageTypes';
import { DetailsWidget } from '../../widgets/detailsWidget';
import { getPerilInclusionsWidgets } from '../../widgets/perilInclusionsWidget';
import {
  createDetailsCard,
  createExcessesTables,
  createPolicyWidgetCard,
  WidgetItem
} from '../../widgets/policyCommonWidgets';
import { createShowPolicyDetailsModalEvent } from './checkYourCoverCustomControl';
import { PerilInformationCustomData } from './perilInformationCustomControl';

export interface CoverSummaryCustomControlProps extends HpButtonListControlProps {
  pageProps: HpClaimsQuestionSetPageProps;
}

const compulsoryExcessesTableHeaders = ['Description', 'Amount'];
const voluntaryExcessesTableHeaders = ['Description', 'Amount'];
const docIcon = 'document-icon.svg';

// TODO AL - This will NOT work with optimisation enabled, as it depends on another question from another section.
export function CoverSummaryCustomControl(buttonListProps: CoverSummaryCustomControlProps): JSX.Element {
  const { answers, pageProps, ui, id, viewport, question } = buttonListProps;
  let warningText = '';
  if (ui && ui.helpInfo && ui.helpInfo.helpText) {
    warningText = ui.helpInfo.helpText;
  }

  const { perilInformation } = questionIds;
  const { policies, questions } = pageProps;
  ensureDefined(policies, 'policies');
  const { customData: perilInformationCustomData } = questions[perilInformation].data as HpClientQuestionData;
  const perilCustomData = ensureDefined(
    perilInformationCustomData,
    'perilInformationCustomData'
  ) as unknown as PerilInformationCustomData;
  if (!perilCustomData) {
    throw new Error(`Question ${perilInformation} does not exist or does not have a custom data object.`);
  }

  const commonCustomData = pageProps.commonCustomData as unknown as HpClaimsCommonCustomData;
  const { copyText } = commonCustomData;
  const { policyCoverModal } = copyText;
  ensureDefined(commonCustomData, 'commonCustomData');
  const policyReference = answers[questionIds.policyInstance].customer as string;
  const selectedPolicy = policies[policyReference];
  const yourCoverCard = createYourCoverCard(perilCustomData, answers, selectedPolicy, commonCustomData);
  const { excesses, endorsements } = selectedPolicy;
  const { compulsoryExcessesTable, voluntaryExcessesTable } = createExcessesTables(
    excesses,
    compulsoryExcessesTableHeaders,
    voluntaryExcessesTableHeaders
  );

  const onChangeHandler = createShowPolicyDetailsModalEvent({
    question,
    id,
    policies,
    policyModalCopyText: policyCoverModal,
    viewport
  });

  const changeEvent = createStandardFormEvent(onChangeHandler, question);

  const policyExcessEventKey = 'policyExcesses';
  const compulsoryExcessesWidget = (
    <DetailsWidget
      heading="Compulsory excesses"
      body={compulsoryExcessesTable}
      eventKey={policyExcessEventKey}
      className="hp-cover-details"
    />
  );
  let voluntaryExcessesWidget: JSX.Element | null = null;
  if (voluntaryExcessesTable) {
    voluntaryExcessesWidget = (
      <DetailsWidget
        heading="Voluntary excesses"
        body={voluntaryExcessesTable}
        eventKey={policyExcessEventKey}
        className="hp-cover-details"
      />
    );
  }

  const excessesCard = createPolicyWidgetCard({
    heading: (
      <div className="hp-heading-control">
        <h2>Policy excesses</h2>
      </div>
    ),
    body: 'This is the amount you will pay if your claim is successful. The total excess you will pay is the sum of the relevant compulsory excesses and voluntary excesses (if you have any).',
    policyItemDetails: (
      <>
        {compulsoryExcessesWidget}
        {voluntaryExcessesWidget}
      </>
    )
  });

  const endorsementsCard = createDetailsCard({
    items: endorsements,
    heading: (
      <div className="hp-heading-control">
        <h2>Your endorsements</h2>
      </div>
    ),
    body: 'An endorsement is a separate term, condition, or extension of cover that applies to your policy. An endorsement takes priority over any of the terms in your policy booklet.',
    eventKey: 'yourEndorsements',
    className: 'hp-cover-details'
  });

  return (
    <Row className="hp-cover-summary-control">
      <div className="cover-summary">
        {warningText && <WarningWidget warningText={warningText} />}
        {yourCoverCard}
        {excessesCard}
        {endorsementsCard}
        <div className="check-cover">
          <span className="summary-text">
            We recommend that your check your policy summary one last time to help you decide if you would like to make
            a claim.
          </span>
          <a onClick={changeEvent} id={policyReference} className="open-modal-link">
            <ImageWidget selected={false} image={docIcon} altText="document icon" />
            <span className="open-modal-text">View policy summary</span>
          </a>
        </div>
        <div className="hp-heading-control">
          <h3>Would you like to make a claim?</h3>
        </div>
      </div>
    </Row>
  );
}

interface CreateCoverDetailsCardProps {
  coverInclusionsCard: JSX.Element;
  coverTypeTitle: string | null;
}

function createYourCoverCard(
  perilCustomData: PerilInformationCustomData,
  answers: HpAnswerMap,
  policy: Policy,
  commonCustomData: HpClaimsCommonCustomData
): JSX.Element {
  const { perilInformationWidget } = getPerilInclusionsWidgets({
    ...perilCustomData,
    answers,
    policy,
    commonCustomData
  });
  const { coverInclusionsTitle, coverExclusionsTitle } = perilCustomData;
  const showCoverTitle = perilInformationWidget.length > 1;
  const coveredCard = (
    <>
      {perilInformationWidget.map((widget, index) => {
        const { whatsCovered, coverTypeTitle } = widget;
        const card = createCoverDetailsCard({
          coverInclusionsCard: whatsCovered,
          coverTypeTitle: showCoverTitle ? coverTypeTitle : null
        });
        return <div key={index}>{card}</div>;
      })}
    </>
  );

  // TODO AL - Simplify and move out to function.
  const notCoveredCard = perilInformationWidget.reduce((coverDetailsCardArray: JSX.Element[], widget) => {
    const { whatsNotCovered, coverTypeTitle } = widget;
    if (whatsNotCovered) {
      const coverDetailsCard = createCoverDetailsCard({
        coverInclusionsCard: whatsNotCovered,
        coverTypeTitle: showCoverTitle ? coverTypeTitle : null
      });
      coverDetailsCardArray.push(coverDetailsCard);
    }

    return coverDetailsCardArray;
  }, []);
  const coveredNotCoveredCard: WidgetItem[] = [{ name: coverInclusionsTitle, description: coveredCard, isHtml: true }];
  if (notCoveredCard.length) {
    coveredNotCoveredCard.push({
      name: coverExclusionsTitle,
      description: <>{notCoveredCard}</>,
      isHtml: true,
      key: coverExclusionsTitle
    });
  }

  return createDetailsCard({
    items: coveredNotCoveredCard,
    heading: (
      <div className="hp-heading-control">
        <h2>Your cover</h2>
      </div>
    ),
    body: `We've listed out areas that are covered and not covered based on the type of incident you've had. Remember to also check endorsements below as they take priority over cover levels.`,
    eventKey: 'yourCover',
    className: 'hp-cover-details'
  });
}

function createCoverDetailsCard({ coverInclusionsCard, coverTypeTitle }: CreateCoverDetailsCardProps) {
  return (
    <div className="summary-cover-details-card hp-list-card">
      {coverTypeTitle && <strong>{coverTypeTitle}</strong>}
      {coverInclusionsCard}
    </div>
  );
}

export function WarningWidget({
  warningText,
  warningHeader
}: {
  warningText: string;
  warningHeader?: string;
}): JSX.Element {
  return (
    <div className="hp-warning-container">
      <SvgIcon type={'warning-amber' as HpSvgIconTypes} />
      {warningHeader && <strong>{warningHeader}</strong>}
      <div className="hp-warning-text" dangerouslySetInnerHTML={{ __html: warningText }}></div>
    </div>
  );
}
